// =============================================================================
// Gate de licencia para la app nativa (caja). En web no hace nada (deja pasar).
// En nativo: valida el machine_id; si no está activado, ofrece activarlo
// (consume cupo de cajas del plan). Bloquea el uso hasta activar.
// =============================================================================
import { useEffect, useState, type ReactNode } from "react";
import { isNative } from "../lib/hardware/printer";
import { validateLicense, activateLicense } from "../lib/hardware/license";
import { useSession } from "../lib/session";

type State = { phase: "checking" | "ok" | "needs-activation" | "error"; reason?: string };

export function LicenseGate({ children }: { children: ReactNode }) {
  const { activeBranchId } = useSession();
  const [state, setState] = useState<State>({ phase: isNative() ? "checking" : "ok" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isNative()) return;
    let alive = true;
    validateLicense().then((r) => {
      if (!alive) return;
      if (r.valid) setState({ phase: "ok" });
      else if (r.reason === "not_activated") setState({ phase: "needs-activation" });
      else setState({ phase: "error", reason: r.reason });
    });
    return () => { alive = false; };
  }, []);

  if (state.phase === "ok") return <>{children}</>;
  if (state.phase === "checking") return <Center msg="Verificando licencia del equipo…" />;

  async function activate() {
    setBusy(true);
    try {
      await activateLicense(activeBranchId ?? undefined, "Caja");
      setState({ phase: "ok" });
    } catch (e) {
      setState({ phase: "error", reason: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-grafito-900 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-xl">
        <div className="mb-2 text-2xl font-extrabold">🔧 Activar esta caja</div>
        <p className="mb-6 text-sm text-grafito-400">
          {state.phase === "error"
            ? state.reason
            : "Este equipo aún no está activado para usar FerreSaaS. Actívalo para empezar a vender. Consume un cupo de caja de tu plan."}
        </p>
        <button onClick={activate} disabled={busy} className="btn btn-primary w-full py-2.5 disabled:opacity-50">
          {busy ? "Activando…" : "Activar caja"}
        </button>
      </div>
    </div>
  );
}

function Center({ msg }: { msg: string }) {
  return <div className="flex min-h-screen items-center justify-center text-grafito-400">{msg}</div>;
}
